// src/app/services/payments/orders/index.tsx
import api, {
  buildQueryParams,
  IGetQueryParams,
  IPaginatedRepsonse,
} from '../..';
import {
  IcreateOrder,
  IOrder,
  ISubscribedServicesItems,
} from 'app/services/responseTypes';

/* ------------------------------------------------------------------ */
/*  Types for the two endpoints                                       */
/* ------------------------------------------------------------------ */
export interface IGetSubscribedServicesItemsQueryParams {
  state?: 'pending_payment' | 'paid' | 'cancelled';
  service_id?: number;
  client_id?: number;
  product_type?: 'service' | 'project';
}

export interface IGetOrdersItemsQueryParams {
  state?: 'pending_payment' | 'paid' | 'cancelled';
  service_id?: number;
  client_id?: number;
}

export interface IGetPromocode {
  code: string;
  percentage: number;
  valid_from: Date;
  valid_to: Date;
}

/* ------------------------------------------------------------------ */
/*  API slice                                                         */
/* ------------------------------------------------------------------ */
export const OrdersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /* ----- Orders ---------------------------------------------------- */
    getOrders: builder.query<IPaginatedRepsonse<IOrder>, IGetQueryParams<{}>>({
      query: (params) => `orders${buildQueryParams(params)}`,
      providesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    /* ----- Subscribed service / bundle items ------------------------ */
    getSubscribedServicesItems: builder.query<
      IPaginatedRepsonse<ISubscribedServicesItems>,
      IGetQueryParams<IGetSubscribedServicesItemsQueryParams>
    >({
      query: (params) => `order_items${buildQueryParams(params)}`,
    }),

    /* ----- Order creation / payment --------------------------------- */
    createOrder: builder.mutation<{ order: IOrder }, IcreateOrder>({
      query: (order) => ({
        url: `/orders`,
        method: 'POST',
        body: order,
      }),
      invalidatesTags: [{ type: 'Cart', id: 'LIST' }],
    }),

    checkoutOrder: builder.mutation<{ redirect_url: string }, string>({
      query: (id) => ({
        url: `/orders/${id}/checkout`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    retryPayment: builder.mutation({
      query: (id: string) => ({
        url: `/orders/${id}/retry_payment`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    cancelService: builder.mutation({
      query: (id: number) => ({
        url: `/order_items/${id}/cancel`,
        method: 'PUT',
      }),
    }),

    /* ----- Stats ----------------------------------------------------- */
    getTaxReturnRevenue: builder.query<
      {
        tax_returns_fees: {
          individual_tax_returns_fees: number;
          business_tax_returns_fees: number;
        };
      },
      IGetQueryParams<{ year?: number }>
    >({
      query: (params) => `/order_items/stats${buildQueryParams(params)}`,
    }),
  }),
});

/* ------------------------------------------------------------------ */
/*  Exported hooks                                                    */
/* ------------------------------------------------------------------ */
export const {
  /* Orders */
  useGetOrdersQuery,
  useLazyGetOrdersQuery,

  /* Subscribed‑services items */
  useGetSubscribedServicesItemsQuery, // ✅ now exported
  useLazyGetSubscribedServicesItemsQuery,

  /* Stats */
  useGetTaxReturnRevenueQuery,

  /* Mutations */
  useCheckoutOrderMutation,
  useCreateOrderMutation,
  useRetryPaymentMutation,
  useCancelServiceMutation,
} = OrdersApi;
